import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, message, Popconfirm, Tooltip, Input, Row, Col, Modal, Typography } from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  HistoryOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/auth';
import scheduledSopService from '../../services/scheduledSopService';
import dayjs from 'dayjs';

const { Title } = Typography;

const ScheduledSopTemplates = () => {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [calculatingTask, setCalculatingTask] = useState(false);
  
  // 排序状态
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // 获取所有定时SOP模板
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await scheduledSopService.getAllScheduledSopTemplates(tenant.id, sortBy, sortOrder);
      setTemplates(data);
    } catch (error) {
      console.error('获取聚合群发列表失败:', error);
      message.error('获取聚合群发列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 创建新模板
  const handleCreate = () => {
    navigate('/scheduled-sop-templates/new');
  };
  
  // 编辑或查看模板，同一个路由，但在目标页面根据模板状态区分只读模式(激活状态)或编辑模式(非激活状态)
  const handleEdit = (id) => {
    navigate(`/scheduled-sop-templates/${id}`);
  };
  
  // 查看执行情况
  const handleViewExecutions = (id) => {
    navigate(`/scheduled-sop-templates/${id}`, { state: { tab: 'executionStatus' } });
  };
  
  // 删除模板
  const handleDelete = async (id) => {
    try {
      Modal.confirm({
        title: '确认删除',
        content: '确定要删除此聚合群发吗？',
        okText: '确认删除',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await scheduledSopService.deleteScheduledSop(id, tenant.id);
            message.success('删除成功');
            fetchTemplates();
          } catch (error) {
            console.error('删除聚合群发失败:', error);
            message.error('删除失败');
          }
        }
      });
    } catch (error) {
      console.error('删除聚合群发失败:', error);
      message.error('删除失败');
    }
  };
  
  // 复制模板
  const handleCopy = async (id, name) => {
    try {
      // 修改代码生成逻辑，确保不超过50个字符
      // 将id转为字符串，以防id是数字
      const originalCode = id.toString();
      // 使用较短的时间戳格式：月日时分
      const timestamp = dayjs().format('MMDDHHmm');
      
      // 计算可用于原始代码的最大长度 (50字符限制减去"_copy_"和时间戳的长度)
      const maxOriginalCodeLength = 50 - 6 - timestamp.length;
      
      // 截取合适长度的原始代码
      const truncatedCode = originalCode.substring(0, maxOriginalCodeLength);
      const newCode = `${truncatedCode}_copy_${timestamp}`;
      
      const newName = `${name} 副本`;
      
      await scheduledSopService.copyScheduledSop(id, {
        newTemplateCode: newCode,
        newTemplateName: newName
      }, tenant.id);
      
      message.success('复制成功');
      fetchTemplates();
    } catch (error) {
      console.error('复制群发失败:', error);
      message.error('复制失败');
    }
  };
  
  // 激活模板
  const handleActivate = async (id) => {
    try {
      await scheduledSopService.activateScheduledSop(id, tenant.id);
      message.success('激活成功');
      fetchTemplates();
    } catch (error) {
      console.error('激活群发失败:', error);
      message.error('激活失败');
    }
  };
  
  // 禁用模板
  const handleDeactivate = async (id) => {
    try {
      await scheduledSopService.deactivateScheduledSop(id, tenant.id);
      message.success('禁用成功');
      fetchTemplates();
    } catch (error) {
      console.error('禁用群发失败:', error);
      message.error('禁用失败');
    }
  };
  
  // 过滤模板
  const filteredTemplates = templates.filter(
    template => 
      template.name.toLowerCase().includes(searchText.toLowerCase()) ||
      template.code.toLowerCase().includes(searchText.toLowerCase()) ||
      (template.description && template.description.toLowerCase().includes(searchText.toLowerCase()))
  );
  
  // 当排序参数变化时重新获取数据
  useEffect(() => {
    if (tenant?.id) {
      fetchTemplates();
    }
  }, [sortBy, sortOrder, tenant?.id]);
  
  // 处理表格变化（包括排序）
  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter && sorter.field) {
      const field = sorter.field;
      let order = 'desc';  // 默认降序
      
      if (sorter.order === 'ascend') {
        order = 'asc';
      } else if (sorter.order === 'descend') {
        order = 'desc';
      }
      
      setSortBy(field);
      setSortOrder(order);
    }
  };
  
  // 处理搜索
  const handleSearch = (value) => {
    setSearchText(value);
    fetchTemplates();
  };
  
  // 表格列配置
  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <span>{text}</span>
          {record.status === 'active' && <Tag color="green">已激活</Tag>}
        </Space>
      )
    },
    {
      title: '目标类型',
      dataIndex: 'targetType',
      key: 'targetType',
      render: (text) => text === 'CUSTOMER' ? '客户' : '客户群',
    },
    {
      title: '步骤数',
      dataIndex: 'stepCount',
      key: 'stepCount',
      align: 'center',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortOrder: sortBy === 'createdAt' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '',
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      sortOrder: sortBy === 'updatedAt' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '',
    },
    {
      title: '操作',
      key: 'action',
      width: 200, // 加宽操作列以适应新增的按钮
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="link"
            onClick={() => handleEdit(record.id)}
            disabled={false}
          >
            {record.status === 'active' ? "查看" : "编辑"}
          </Button>
          
          <Button 
            type="link"
            onClick={() => handleViewExecutions(record.id)}
          >
            执行情况
          </Button>
          
          <Button 
            type="link"
            onClick={() => handleCopy(record.id, record.name)}
          >
            复制
          </Button>
          
          {record.status !== 'active' ? (
            <Button 
              type="link"
              onClick={() => handleActivate(record.id)}
            >
              激活
            </Button>
          ) : (
            <Button 
              type="link"
              onClick={() => handleDeactivate(record.id)}
            >
              禁用
            </Button>
          )}
          
          <Button 
            type="link"
            danger
            onClick={() => handleDelete(record.id)}
            disabled={record.status === 'active'}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];
  
  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>聚合群发</Title>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <Input.Search
                  placeholder="搜索群发名称"
                  allowClear
                  enterButton
                  onSearch={handleSearch}
                  style={{ width: 250 }}
                />
              </Space>
            </Col>
          </Row>
          <Row>
            <Col>
              <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                  新建聚合群发
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredTemplates}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default ScheduledSopTemplates; 